import { useFormContext } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";

import { useGetCountries } from "api/queries/common/useGetCountries";
import Button from "components/atoms/Button/Button";
import DatePicker from "components/atoms/DatePicker/DatePicker";
import TimeInput from "components/atoms/TimeInput/TimeInput";
import FormField from "components/molecules/FormField/FormField";
import SearchSelect from "pages/shipper/RequestForm/components/Common/SearchSelect";
import { useGetAirlineAndPortOptions } from "helpers/editScheduleHelpers";
import useSearchQuery from "hooks/useSearchQuery";
import { IAirportAddress } from "pages/provider/QuotationForm/components/Schedule/ScheduleTypes";
import { IEditScheduleForm } from "../types";

interface FlightFormProps {
  index: number;
  onRemove: (index: number) => void;
}

export default function FlightForm({ index, onRemove }: FlightFormProps) {
  const intl = useIntl();
  const key = `flights.${index}`;

  const {
    setValue,
    formState: { errors },
    watch,
    clearErrors,
  } = useFormContext<IEditScheduleForm>();

  const [isManual, route, isDelete] = watch(
    ["is_manual", "route", "delete"].map(
      (fieldname) => `flights.${index}.${fieldname}` as keyof IEditScheduleForm["flights"],
    ),
  );

  const { data: countriesData } = useGetCountries({
    refetchOnMount: false,
  });

  const [departureCity, arrivalCity] = watch([
    `flights.${index}.departure_airport_city_name`,
    `flights.${index}.arrival_airport_city_name`,
  ]);
  const departureCountry =
    watch(`flights.${index}.departure_airport_country_name`) ??
    countriesData?.find((country) => country.value === watch(`flights.${index}.departure_airport_country_code`))?.label;
  const arrivalCountry =
    watch(`flights.${index}.arrival_airport_country_name`) ??
    countriesData?.find((country) => country.value === watch(`flights.${index}.arrival_airport_country_code`))?.label;

  const handleRemove = () => {
    if (hasPrefilledData) {
      setValue(`flights.${index}.delete`, true);
      clearErrors(`flights.${index}`);
      return;
    }
    onRemove(index);
  };

  const { query: airlinesQuery, handleInputChange: handleAirlineInputChange } = useSearchQuery();
  const { query: departurePortQuery, handleInputChange: handleDepartureInputChange } = useSearchQuery();
  const { query: arrivalPortQuery, handleInputChange: handleArrivalInputChange } = useSearchQuery();

  const {
    airlineOptions,
    departurePortOptions,
    arrivalPortOptions,
    autoFlightData,
    autoFlightOptions,
    autoFlightDataWithRoute,
    hasPrefilledData,
    isFetchingRoute,
  } = useGetAirlineAndPortOptions({
    airlinesQuery,
    departurePortQuery,
    arrivalPortQuery,
    index,
  });

  const handleSelectDepartureAirport = (item: any, index: number) => {
    setValue(`flights.${index}.from_airport_name` as keyof IEditScheduleForm, item?.[0]?.label || "");
    setValue(`flights.${index}.departure_airport_city_name` as keyof IEditScheduleForm, item?.[0]?.city_name || "");
    setValue(`flights.${index}.departure_airport_country_name` as keyof IEditScheduleForm, item?.[0]?.country_name || "");
  };
  const handleSelectArrivalAirport = (item: any, index: number) => {
    setValue(`flights.${index}.to_airport_name` as keyof IEditScheduleForm, item?.[0]?.label || "");
    setValue(`flights.${index}.arrival_airport_city_name` as keyof IEditScheduleForm, item?.[0]?.city_name || "");
    setValue(`flights.${index}.arrival_airport_country_name` as keyof IEditScheduleForm, item?.[0]?.country_name || "");
  };

  if (isDelete) return null;

  return (
    <>
      <div className="d-flex">
        <SearchSelect
          classes="w-192"
          options={airlineOptions}
          name={`${key}.airline_code`}
          label={intl.formatMessage({
            id: "tracking.components.editScheduleDialog.airline",
            defaultMessage: "Airline",
          })}
          placeholder={intl.formatMessage({
            id: "general.search",
            defaultMessage: "Search",
          })}
          onInputChange={handleAirlineInputChange}
          required
        />
        <FormField
          containerClasses="w-92 ml-3"
          id={`${key}.flight_number`}
          field_id={`${key}.flight_number`}
          label={intl.formatMessage({
            id: "tracking.components.editScheduleDialog.flightNumber",
            defaultMessage: "Flight number",
          })}
          eventContext="Edit Schedule Form"
          type="textinput"
          mandatory
        />
        <Button className="ml-3 mt-24 height-fit" variant="danger" onClick={handleRemove}>
          <FormattedMessage id="general.remove" defaultMessage="Remove" />
        </Button>
      </div>

      <div className="d-flex">
        <div>
          <label className="required">
            <FormattedMessage id="tracking.components.editScheduleDialog.departureDate" defaultMessage="Departure date" />
          </label>
          <DatePicker wrapperClassNames="w-192" required name={`${key}.departure_date`} />
        </div>
        {isManual && (
          <div className="ml-3 mr-3">
              <label className="required">
                <FormattedMessage
                  id="tracking.components.editScheduleDialog.departureTime"
                  defaultMessage="Departure time"
                />
              </label>
              <TimeInput name={`${key}.departure_time`} required outputFormat="HH:mm" />
            </div>
        )}
      </div>

      {isManual && (
        <div className="d-flex">
          <SearchSelect
            options={departurePortOptions}
            name={`${key}.from_airport_code`}
            label={intl.formatMessage({
              id: "tracking.components.editScheduleDialog.departureAirport",
              defaultMessage: "Departure airport",
            })}
            placeholder={intl.formatMessage({
              id: "general.search",
              defaultMessage: "Search",
            })}
            onInputChange={handleDepartureInputChange}
            onSelect={(item: IAirportAddress[]) => {
              handleSelectDepartureAirport(item, index);
            }}
            required
          />
          {(departureCity || departureCountry) && (
            <div className="ml-3">
              <label>
                {intl.formatMessage({
                  id: "tracking.components.editScheduleDialog.departureCity",
                  defaultMessage: "Departure city",
                })}
              </label>
              <div className="font-b4">
                {departureCity}
                {departureCity && departureCountry ? ", " : ""}
                {departureCountry}
              </div>
            </div>
          )}
        </div>
      )}

      {isManual && (
        <div className="d-flex">
          <div>
            <label className="required">
              <FormattedMessage id="tracking.components.editScheduleDialog.arrivalDate" defaultMessage="Arrival date" />
            </label>
            <DatePicker wrapperClassNames="w-192" required name={`${key}.arrival_date`} />
          </div>
          <div className="ml-3 mr-3">
            <label className="required">
              <FormattedMessage id="tracking.components.editScheduleDialog.arrivalTime" defaultMessage="Arrival time" />
            </label>
            <TimeInput name={`${key}.arrival_time`} required outputFormat="HH:mm" />
          </div>
        </div>
      )}

      {isManual && (
        <div className="d-flex">
          <SearchSelect
            options={arrivalPortOptions}
            name={`${key}.to_airport_code`}
            label={intl.formatMessage({
              id: "tracking.components.editScheduleDialog.arrivalAirport",
              defaultMessage: "Arrival airport",
            })}
            placeholder={intl.formatMessage({
              id: "general.search",
              defaultMessage: "Search",
            })}
            onInputChange={handleArrivalInputChange}
            onSelect={(item: IAirportAddress[]) => {
              handleSelectArrivalAirport(item, index);
            }}
            required
          />
          {(arrivalCity || arrivalCountry) && (
            <div className="ml-3">
              <label>
                {intl.formatMessage({
                  id: "tracking.components.editScheduleDialog.arrivalCity",
                  defaultMessage: "Arrival City",
                })}
              </label>
              <div className="font-b4">
                {arrivalCity}
                {arrivalCity && arrivalCountry ? ", " : ""}
                {arrivalCountry}
              </div>
            </div>
          )}
        </div>
      )}

      {!isManual && (autoFlightData?.has_more_route || autoFlightData?.routes || route) && (
        <SearchSelect
          options={autoFlightOptions}
          name={`${key}.route`}
          label={intl.formatMessage({
            id: "tracking.components.editScheduleDialog.route",
            defaultMessage: "Route",
          })}
          placeholder={intl.formatMessage({
            id: "general.select",
            defaultMessage: "Select",
          })}
          required
        />
      )}

      {!isManual && Boolean(errors?.flights?.[index]?.error) && (
        <div className="d-block font-b3 invalid-feedback">{errors?.flights?.[index]?.error?.message}</div>
      )}

      {!isManual && autoFlightDataWithRoute?.two_line_flight_summary && autoFlightDataWithRoute?.on_time_text && (
        <div
          className="p-3 bg-warning d-flex justify-content-between align-items-end text-body"
          style={{ borderRadius: "12px" }}
        >
          <div className="font-b3">{autoFlightDataWithRoute?.two_line_flight_summary}</div>
          <div className="font-b3">{autoFlightDataWithRoute?.on_time_text}</div>
        </div>
      )}

      {isFetchingRoute && <div className="spinner-border spinner-border-sm mt-xl" role="status" />}

      {!isManual && (
        <Button
          className="mt-4"
          size="sm"
          variant="outline-primary"
          onClick={() => setValue(`flights.${index}.is_manual`, true)}
        >
          <FormattedMessage id="tracking.components.editScheduleDialog.editManually" defaultMessage="Edit manually" />
        </Button>
      )}

      {isManual && (
        <Button
          className="mt-4"
          size="sm"
          variant="outline-primary"
          onClick={() => setValue(`flights.${index}.is_manual`, false)}
        >
          <FormattedMessage id="tracking.components.editScheduleDialog.automaticLookup" defaultMessage="Automatic lookup" />
        </Button>
      )}
      <hr className="my-4" />
    </>
  );
}
